import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const NamePopularityCalculator = () => {
  const [nameData, setNameData] = useState({
    name: '',
    gender: 'any',
    startYear: '1900',
    endYear: '2025'
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNameData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculatePopularity = (e) => {
    e.preventDefault(); // Prevent the default form submission behavior

    const { name, gender, startYear, endYear } = nameData;
    
    if (!name) {
      setError('Please enter a name');
      return;
    }

    // Mock popularity data (in a real application, this would come from an API)
    const mockData = generateMockPopularityData(name, parseInt(startYear), parseInt(endYear));

    const newResults = {
      ...mockData,
      calculationSteps: [
        'Step 1: Data Collection',
        `Name: ${name}`,
        `Gender: ${gender}`,
        `Time period: ${startYear} to ${endYear}`,
        '\nStep 2: Popularity Analysis',
        `Highest rank: ${mockData.highestRank}`,
        `Highest year: ${mockData.highestYear}`,
        `Lowest rank: ${mockData.lowestRank}`,
        `Lowest year: ${mockData.lowestYear}`,
        '\nStep 3: Trend Analysis',
        `Current trend: ${mockData.currentTrend}`,
        `Overall popularity: ${mockData.overallPopularity}`
      ]
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      labels: mockData.years,
      datasets: [{
        label: 'Popularity Rank',
        data: mockData.ranks,
        fill: false,
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1,
        pointRadius: 4,
        pointHoverRadius: 6
      }]
    });
  };

  const generateMockPopularityData = (name, startYear, endYear) => {
    const years = [];
    const ranks = [];
    
    // Calculate a base rank based on the name length and characters
    let baseRank = 500; // Start with a moderate rank
    
    // Adjust base rank based on name characteristics
    const nameLength = name.length;
    const vowels = (name.match(/[aeiou]/gi) || []).length;
    const consonants = nameLength - vowels;
    
    // Use consistent factors based on name characteristics
    baseRank -= nameLength * 10; // Longer names tend to be less popular
    baseRank -= vowels * 5; // More vowels can make names more popular
    baseRank += consonants * 3; // More consonants can make names less popular
    
    // Ensure rank stays within bounds
    baseRank = Math.max(1, Math.min(1000, baseRank));

    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
      
      // Create a consistent trend based on time period
      if (year < 1950) {
        // Older names tend to have higher ranks
        ranks.push(baseRank + (1950 - year) * 2);
      } else if (year < 2000) {
        // Modern names tend to have lower ranks
        ranks.push(baseRank - (year - 1950) * 2);
      } else {
        // Recent years show stabilization
        ranks.push(baseRank);
      }
    }

    return {
      years,
      ranks,
      highestRank: Math.min(...ranks),
      highestYear: years[ranks.indexOf(Math.min(...ranks))],
      lowestRank: Math.max(...ranks),
      lowestYear: years[ranks.indexOf(Math.max(...ranks))],
      currentTrend: calculateTrend(ranks),
      overallPopularity: calculateOverallPopularity(ranks)
    };
  };

  const calculateTrend = (ranks) => {
    const lastRank = ranks[ranks.length - 1];
    const firstRank = ranks[0];
    
    if (lastRank < firstRank) {
      return 'Rising';
    } else if (lastRank > firstRank) {
      return 'Declining';
    } else {
      return 'Stable';
    }
  };

  const calculateOverallPopularity = (ranks) => {
    const averageRank = ranks.reduce((a, b) => a + b, 0) / ranks.length;
    if (averageRank <= 100) return 'Very Popular';
    if (averageRank <= 500) return 'Popular';
    if (averageRank <= 800) return 'Moderate';
    return 'Less Popular';
  };

  return (
    <>
      <Helmet>
        <title>Name Popularity Calculator | Analyze Name Trends Over Time</title>
        <meta name="description" content="Analyze name popularity trends over time with our Name Popularity Calculator. Get insights into name rankings, historical popularity, and future trends." />
        <meta name="keywords" content="name popularity, name trends, name ranking analysis, historical name data, baby name popularity" />
        <meta property="og:title" content="Name Popularity Calculator | Analyze Name Trends Over Time" />
        <meta property="og:description" content="Analyze name popularity trends over time with our Name Popularity Calculator. Get insights into name rankings, historical popularity, and future trends." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/name-popularity-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-blue-600 mb-2">📊</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Name Popularity Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Analyze the popularity trends of any name over time and get insights into its historical ranking.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form className="space-y-6" onSubmit={calculatePopularity}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  name="name"
                  value={nameData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter a name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <select
                  name="gender"
                  value={nameData.gender}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="any">Any</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Year</label>
                  <select
                    name="startYear"
                    value={nameData.startYear}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Array.from({ length: 126 }, (_, i) => 1900 + i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Year</label>
                  <select
                    name="endYear"
                    value={nameData.endYear}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Array.from({ length: 126 }, (_, i) => 1900 + i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-blue-700 transition duration-200"
              >
                Analyze Popularity
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {results && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Popularity Analysis</h2>
                  
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <h3 className="text-xl font-semibold mb-4">Popularity Trend</h3>
                    <div className="h-96">
                      <Line
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Rank (Lower is More Popular)' },
                              ticks: { stepSize: 100 }
                            },
                            x: { title: { display: true, text: 'Year' } }
                          },
                          plugins: { title: { display: true, text: 'Name Popularity Over Time' } }
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <h3 className="text-xl font-semibold mb-2">Highest Popularity</h3>
                      <p className="text-lg font-medium">Rank: {results.highestRank}</p>
                      <p className="text-lg font-medium">Year: {results.highestYear}</p>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <h3 className="text-xl font-semibold mb-2">Lowest Popularity</h3>
                      <p className="text-lg font-medium">Rank: {results.lowestRank}</p>
                      <p className="text-lg font-medium">Year: {results.lowestYear}</p>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <h3 className="text-xl font-semibold mb-2">Current Trend</h3>
                      <p className="text-lg font-medium">{results.currentTrend}</p>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <h3 className="text-xl font-semibold mb-2">Overall Popularity</h3>
                      <p className="text-lg font-medium">{results.overallPopularity}</p>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <h3 className="text-xl font-semibold mb-4">Analysis Steps</h3>
                    <div className="space-y-4">
                      {results.calculationSteps.map((step, index) => (
                        <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                          <pre className="whitespace-pre-wrap break-words font-mono text-sm">
                            {step}
                          </pre>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto mt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use</h2>
            <p className="text-gray-700">
              To analyze the popularity of a name, follow these steps:
            </p>
            <ol className="list-decimal pl-6 space-y-2">
              <li>Enter the name you want to analyze.</li>
              <li>Select the gender (optional).</li>
              <li>Choose the time period for analysis.</li>
              <li>Click "Analyze Popularity" to see the results.</li>
              <li>View the popularity trend chart and analysis results.</li>
            </ol>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto mt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Features & Benefits</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Historical Analysis</h3>
                <p className="text-gray-700">
                  Analyze name popularity trends over multiple decades.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Visual Insights</h3>
                <p className="text-gray-700">
                  View interactive charts showing name ranking changes over time.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Comprehensive Metrics</h3>
                <p className="text-gray-700">
                  Get detailed insights including highest/lowest rankings and current trends.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Gender Analysis</h3>
                <p className="text-gray-700">
                  Analyze name popularity separately for male and female names.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto mt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQs</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800">What is name popularity?</h3>
                <p className="text-gray-700">
                  Name popularity refers to how frequently a name is given to babies in a particular year.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">How is popularity measured?</h3>
                <p className="text-gray-700">
                  Popularity is measured using a ranking system where lower numbers indicate more popular names.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">What does the trend chart show?</h3>
                <p className="text-gray-700">
                  The trend chart shows how a name's popularity has changed over time, with lower ranks indicating higher popularity.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Can I analyze names from different time periods?</h3>
                <p className="text-gray-700">
                  Yes, you can select any time period between 1900 and 2025 to analyze name popularity trends.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NamePopularityCalculator;